import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Star, Play, Clock, Lock, Check, ShoppingCart, Headphones, BookOpen, Calendar, Globe as GlobeIcon, Heart, Layers, Loader2,
} from "lucide-react";
import { CATEGORIES, formatDuration, formatTime } from "../mock";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { useCatalog } from "../context/CatalogContext";
import { usePlayer } from "../context/PlayerContext";
import { Button } from "../components/ui/button";
import { useToast } from "../hooks/use-toast";
import BookCard from "../components/BookCard";
import BookCover from "../components/BookCover";
import Reviews from "../components/Reviews";

const LANGUAGE_NAMES = { en: "English", es: "Español" };

export default function BookDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLang();
  const { user, hasBook, addToLibrary, toggleWishlist, inWishlist } = useAuth();
  const { getBook, fetchBook, books: BOOKS, loading } = useCatalog();
  const { playTrack, current } = usePlayer();
  const { toast } = useToast();

  const [fetched, setFetched] = React.useState(null);
  const [buying, setBuying] = React.useState(false);
  const book = getBook(id) || fetched;

  React.useEffect(() => {
    if (!getBook(id) && !loading) {
      fetchBook(id).then(setFetched);
    }
  }, [id, loading]); // eslint-disable-line

  if (!book) {
    return (
      <div className="py-32 text-center text-muted-foreground">
        {loading ? "…" : "Not found"}
      </div>
    );
  }

  const owned = user && hasBook(book.id);
  const title = book.title_en;
  const cat = CATEGORIES.find((c) => c.id === book.category);
  const related = BOOKS.filter((b) => b.category === book.category && b.id !== book.id).slice(0, 6);

  const handlePurchase = async () => {
    if (!user) return navigate(`/login?next=/book/${book.slug || book.id}`);
    if (buying) return;
    setBuying(true);
    // The result is checked now: this used to announce success whether the
    // audiobook had been added or the request had failed.
    const res = await addToLibrary(book.id);
    setBuying(false);
    if (res?.ok) {
      toast({ title: t("purchase_success") });
    } else {
      toast({
        title: "We could not complete that",
        description: res?.error,
        variant: "destructive",
      });
    }
  };

  const playChapter = (chapter) => {
    if (owned) playTrack(book, chapter, false);
    else playTrack(book, null, true);
  };

  return (
    <div className="animate-float-up pb-24">
      {/* hero backdrop */}
      <div className="relative">
        <div className="absolute inset-0 h-[420px] overflow-hidden">
          {book.coverPath ? (
            <img src={book.cover} alt="" className="w-full h-full object-cover blur-2xl scale-110 opacity-30" />
          ) : (
            <div className="w-full h-full" style={{ background: "radial-gradient(60% 80% at 20% 0%, rgba(245,179,66,0.10), transparent 70%)" }} />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/85 to-background" />
        </div>

        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 pt-10">
          <div className="flex flex-col md:flex-row gap-8 lg:gap-12">
            {/* cover */}
            <div className="shrink-0 mx-auto md:mx-0">
              <div className="w-[240px] sm:w-[280px] rounded-2xl overflow-hidden cover-shadow aspect-[2/3]">
                <BookCover book={book} className="w-full h-full" />
              </div>
            </div>

            {/* info */}
            <div className="flex-1 min-w-0">
              <button
                onClick={() => navigate(`/browse?cat=${book.category}`)}
                className="inline-flex items-center px-3 py-1 rounded-full bg-brand-500/15 border border-brand-500/30 text-brand-300 text-xs font-semibold mb-4"
              >
                {cat?.en}
              </button>
              <h1 className="font-display text-3xl sm:text-5xl font-semibold tracking-tight leading-tight">{title}</h1>
              {/* Optional, and most books have none. When it is there it is the
                  fastest route to the next purchase, so it sits under the title
                  rather than buried in the details row. */}
              {book.series && (
                <Link
                  to={`/series/${encodeURIComponent(book.series)}`}
                  className="inline-flex items-center gap-1.5 mt-3 text-sm text-brand-400 hover:text-brand-300 transition-colors"
                >
                  <Layers className="w-4 h-4" />
                  {book.seriesPosition
                    ? `Book ${book.seriesPosition} of the ${book.series} series`
                    : `Part of the ${book.series} series`}
                </Link>
              )}
              <div className="mt-3 space-y-1">
                {/* Links, not buttons: these are the two routes a crawler
                    should follow out of a book page, and the two a reader
                    expects to be able to open in a new tab. */}
                <p className="text-muted-foreground">
                  {t("written_by")}{" "}
                  <Link
                    to={`/author/${encodeURIComponent(book.author)}`}
                    className="text-foreground font-medium hover:text-brand-400 transition-colors"
                  >
                    {book.author}
                  </Link>
                </p>
                <p className="text-muted-foreground flex items-center gap-1.5">
                  <Headphones className="w-4 h-4" /> {t("narrated_by")}{" "}
                  <Link
                    to={`/narrator/${encodeURIComponent(book.narrator)}`}
                    className="text-foreground font-medium hover:text-brand-400 transition-colors"
                  >
                    {book.narrator}
                  </Link>
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-5 text-sm">
                <span className="flex items-center gap-1.5 text-brand-400 font-semibold">
                  <Star className="w-4 h-4" fill="currentColor" /> {Number(book.rating || 0).toFixed(1)}
                  <span className="text-muted-foreground font-normal">({book.ratingCount} {t("ratings")})</span>
                </span>
                <span className="flex items-center gap-1.5 text-muted-foreground">
                  <Clock className="w-4 h-4" /> {formatDuration(book.duration)}
                </span>
                <span className="flex items-center gap-1.5 text-muted-foreground">
                  <BookOpen className="w-4 h-4" /> {book.chaptersCount} {t("chapters")}
                </span>
                <span className="flex items-center gap-1.5 text-muted-foreground">
                  <Calendar className="w-4 h-4" /> {book.releaseYear}
                </span>
                <span className="flex items-center gap-1.5 text-muted-foreground">
                  <GlobeIcon className="w-4 h-4" /> {LANGUAGE_NAMES[book.language] ?? book.language?.toUpperCase()}
                </span>
              </div>

              {/* actions */}
              <div className="flex flex-wrap items-center gap-3 mt-7">
                {owned ? (
                  <Button
                    onClick={() => playTrack(book, book.chapters[0], false)}
                    className="bg-brand-500 hover:bg-brand-400 text-brand-950 font-semibold rounded-full h-12 px-8 text-base"
                  >
                    <Play className="w-5 h-5 mr-1" fill="currentColor" /> {t("listen_now")}
                  </Button>
                ) : (
                  <>
                    <Button
                      onClick={handlePurchase}
                      disabled={buying}
                      className="bg-brand-500 hover:bg-brand-400 text-brand-950 font-semibold rounded-full h-12 px-7 text-base disabled:opacity-70"
                    >
                      {buying ? (
                        <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Adding…</>
                      ) : (
                        <><ShoppingCart className="w-5 h-5 mr-2" /> {t("buy_now")} · £{Number(book.price).toFixed(2)}</>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => playTrack(book, null, true)}
                      className="rounded-full h-12 px-6 text-base border-white/25 bg-white/5 hover:bg-white/10"
                    >
                      <Play className="w-4 h-4 mr-2" fill="currentColor" /> {t("play_sample")}
                    </Button>
                  </>
                )}
                <Button
                  variant="outline"
                  onClick={async () => {
                    const r = await toggleWishlist(book.id);
                    if (r?.needsAuth) navigate("/login");
                  }}
                  className={`rounded-full h-12 w-12 p-0 border-white/25 bg-white/5 hover:bg-white/10 ${inWishlist(book.id) ? "text-brand-400" : ""}`}
                  title={inWishlist(book.id) ? t("in_wishlist") : t("add_wishlist")}
                >
                  <Heart className="w-5 h-5" fill={inWishlist(book.id) ? "currentColor" : "none"} />
                </Button>
              </div>
              {owned && (
                <p className="flex items-center gap-1.5 text-emerald-400 text-sm mt-3 font-medium">
                  <Check className="w-4 h-4" /> {t("in_library")}
                </p>
              )}
              {!owned && (
                <p className="flex items-center gap-1.5 text-muted-foreground text-xs mt-3">
                  <Lock className="w-3.5 h-3.5" /> {t("unlock_hint")}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* overview + chapters */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 mt-12 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <h2 className="font-display text-2xl font-semibold mb-4">{t("overview")}</h2>
          <p className="text-muted-foreground leading-relaxed whitespace-pre-line">{book.desc_en}</p>
        </div>

        <div className="lg:col-span-1">
          <div className="rounded-2xl border border-border bg-card/50 overflow-hidden">
            <div className="px-5 py-4 border-b border-border flex items-center justify-between">
              <h3 className="font-semibold">{t("chapters")}</h3>
              <span className="text-xs text-muted-foreground">{book.chapters.length}</span>
            </div>
            <div className="max-h-[420px] overflow-y-auto">
              {book.chapters.map((ch) => {
                const active = current?.chapter?.id === ch.id;
                return (
                  <button
                    key={ch.id}
                    onClick={() => playChapter(ch)}
                    className={`w-full flex items-center gap-3 px-5 py-3 text-left border-b border-border/50 last:border-0 transition-colors hover:bg-secondary/60 ${
                      active ? "bg-brand-500/10" : ""
                    }`}
                  >
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${active ? "bg-brand-500 text-brand-950" : "bg-secondary text-muted-foreground"}`}>
                      {owned ? <Play className="w-3.5 h-3.5 ml-0.5" fill="currentColor" /> : ch.order}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm font-medium truncate ${active ? "text-brand-400" : ""}`}>{ch.title_en}</p>
                      <p className="text-xs text-muted-foreground">{formatTime(ch.duration)}</p>
                    </div>
                    {!owned && ch.order > 1 && <Lock className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* reviews */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <Reviews bookId={book.id} owned={owned} headline={{ rating: book.rating, ratingCount: book.ratingCount }} />
      </div>

      {/* related */}
      {related.length > 0 && (
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 mt-16">
          <h2 className="font-display text-2xl font-semibold mb-6">
            You might also like
          </h2>
          <div className="flex gap-5 overflow-x-auto no-scrollbar pb-2">
            {related.map((b) => (
              <BookCard key={b.id} book={b} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
